// Food timing for a prescription item.
//
// WARNING: prescription_items.after_food is a THREE-VALUED column, not a boolean:
//     0 = not set
//     1 = after food                 (หลังอาหาร)
//     2 = immediately after a meal   (หลังอาหารทันที)
//
// The column is tinyint(1), but the (1) is only a display width — the real range
// is -128..127, so 2 stores and round-trips fine.
//
// NEVER write `!!after_food` or `after_food ? 1 : 0`. That collapses 2 back to 1
// and the label on a real medicine bag then says the wrong thing. Use these
// helpers. before_food stays a plain 0/1 and is mutually exclusive with any
// non-zero after_food.
//
// This file is copied verbatim (as ESM) into each React app's src/utils/.

const AFTER_FOOD_NO = 0;
const AFTER_FOOD_YES = 1;
const AFTER_FOOD_IMMEDIATE = 2;

// This system's meal tokens. NOT pet-clinic's breakfast/lunch/dinner set.
const MEAL_TOKENS = ['morning', 'noon', 'evening', 'bedtime'];
const MEAL_LABEL_KEYS = {
  morning: ['meal_morning', 'Morning'],
  noon:    ['meal_noon',    'Noon'],
  evening: ['meal_evening', 'Evening'],
  bedtime: ['meal_bedtime', 'Bedtime'],
};

function toAfterFoodValue(value) {
  const n = Number(value);
  if (n === AFTER_FOOD_IMMEDIATE) return AFTER_FOOD_IMMEDIATE;
  // Check the NUMERIC value's truthiness, not the raw value's: the raw value
  // may be the string '0', which is truthy in JS and would otherwise collapse
  // "not set" into "after food".
  return n ? AFTER_FOOD_YES : AFTER_FOOD_NO;
}

const isAfterFood = (value) => toAfterFoodValue(value) !== AFTER_FOOD_NO;
const isImmediatelyAfterFood = (value) => toAfterFoodValue(value) === AFTER_FOOD_IMMEDIATE;
const hasFoodTiming = (beforeFood, afterFood) => !!Number(beforeFood) || isAfterFood(afterFood);

const FOOD_LABEL_KEYS = {
  take: {
    before:    ['take_before_food', 'Take before food'],
    after:     ['take_after_food', 'Take after food'],
    immediate: ['take_after_food_immediate', 'Take immediately after food'],
  },
  short: {
    before:    ['before_food', 'Before food'],
    after:     ['after_food', 'After food'],
    immediate: ['after_food_immediate', 'Immediately after food'],
  },
};

// style 'take' (default) for printed documents, 'short' for forms and lists.
// Returns '' when nothing is set. before_food wins if a legacy row somehow has
// both, so a label can never carry two contradictory instructions.
function foodTimingLabel(beforeFood, afterFood, print_lang, style = 'take') {
  const keys = FOOD_LABEL_KEYS[style] || FOOD_LABEL_KEYS.take;
  if (Number(beforeFood) === 1) return print_lang(...keys.before);
  const after = toAfterFoodValue(afterFood);
  if (after === AFTER_FOOD_IMMEDIATE) return print_lang(...keys.immediate);
  if (after === AFTER_FOOD_YES) return print_lang(...keys.after);
  return '';
}

// Renders a stored meal_times CSV in canonical order, dropping anything that is
// not one of this system's four tokens.
function mealTimesLabel(csv, print_lang) {
  if (!csv) return '';
  const given = String(csv).split(',').map((s) => s.trim().toLowerCase()).filter(Boolean);
  return MEAL_TOKENS
    .filter((t) => given.includes(t))
    .map((t) => print_lang(...MEAL_LABEL_KEYS[t]))
    .join(', ');
}

export {
  AFTER_FOOD_NO, AFTER_FOOD_YES, AFTER_FOOD_IMMEDIATE, MEAL_TOKENS,
  toAfterFoodValue, isAfterFood, isImmediatelyAfterFood, hasFoodTiming,
  foodTimingLabel, mealTimesLabel,
};
